/**
 * Picking up a new build without anybody clearing their site data.
 *
 * There are two ways the app can be out of date and they need different answers, which is the
 * whole reason this file exists separately from the service worker that half of it drives.
 *
 *   1. **A new service worker.** `sw.js` calls `skipWaiting`, so a new one takes over as soon
 *      as it installs, and `controllerchange` says it has. But a service worker only *checks*
 *      for a new copy of itself on a navigation — and an installed PWA that is opened, used
 *      and backgrounded never has one. So we ask, rather than wait.
 *
 *   2. **A new front end with an unchanged service worker**, which is the ordinary case and
 *      the one the first mechanism silently misses: `sw.js` changes maybe twice a year, the
 *      bundle changes every deploy, and `controllerchange` never fires for it. The signal is
 *      the entry script's fingerprint in `index.html`. It changes exactly when the front end
 *      does, which is exactly when a reload is worth it.
 *
 * Both are checked when the app comes back to the foreground rather than on a timer. That is
 * the moment somebody is demonstrably not halfway through typing, so the reload lands in a gap
 * — and a queued report would survive it either way, the outbox being in IndexedDB, but a
 * half-written note would not.
 */

/** The entry script this page is actually running. */
function runningEntry(): string | undefined {
  const element = document.querySelector<HTMLScriptElement>(
    'script[type="module"][src^="/assets/"]',
  );
  return element?.getAttribute('src') ?? undefined;
}

/** The entry script the server would hand out now. */
async function deployedEntry(): Promise<string | undefined> {
  // `no-store` defeats the HTTP cache; the service worker is network-first for anything
  // without a fingerprint in its name, so this really does reach the server.
  const response = await fetch('/index.html', { cache: 'no-store' });
  if (!response.ok) return undefined;
  return /<script[^>]+src="(\/assets\/index-[^"]+\.js)"/.exec(await response.text())?.[1];
}

export function watchForUpdates(registration: ServiceWorkerRegistration): void {
  const running = runningEntry();
  // `controllerchange` also fires when a page that had no controller gains one, which is every
  // genuinely first visit. Reloading there is a reload nobody asked for, on a page that was
  // already current.
  const hadController = navigator.serviceWorker.controller !== null;
  let reloading = false;

  const reload = () => {
    if (reloading) return;
    reloading = true;
    location.reload();
  };

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (hadController) reload();
  });

  async function check(): Promise<void> {
    void registration.update().catch(() => undefined);
    // Without a script tag to compare against there is nothing to decide, and guessing here
    // would mean a reload loop rather than a stale page.
    if (running === undefined) return;
    const deployed = await deployedEntry().catch(() => undefined);
    if (deployed !== undefined && deployed !== running) reload();
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    void check();
  });
}
